import { store } from '../../../store';
import { ensureResponseOk } from '../../../shared/errors/HttpError';
import type { ApiClient } from '../../auth/api/clients';
import { createCommittedJobNumberItem, jobNumbersAtomFamily, scheduleAtomFamily } from '../atoms/settingAtoms';
import type { CallSetting } from '../types';

// クライアント単位の単一フライトキャッシュ。アプリ実行時はクライアントが1つのため実質1件だが、
// 引数で受けたクライアントに依存する結果をモジュール全体で共有しない（テスト間の持ち越し防止）。
const callSettingsPromises = new WeakMap<ApiClient, Promise<CallSetting[]>>();

const fetchCallSettings = async (apiClient: ApiClient): Promise<CallSetting[]> => {
  const res = await apiClient['call-settings'].$get();
  // guardedFetch はリフレッシュ失敗後の 401 をレスポンスとして返すため、ここで HttpError に変換する。
  await ensureResponseOk(res);
  return res.json();
};

// 編集用 atom に取得結果を初期値として投入する。単一フライトのため初期化は一度きりで、
// 以降の変更（仕事Noの追加削除・スケジュール保存）は atom 側だけを更新する。
const seedEditingAtoms = (settings: CallSetting[]): void => {
  for (const setting of settings) {
    store.set(jobNumbersAtomFamily(setting.id), setting.jobNumbers.map(createCommittedJobNumberItem));
    // current と baseline は同一参照を共有する。更新側（useSchedule）が常にイミュータブルに
    // 更新する前提であり、どちらかをミューテーションすると dirty 判定が壊れる。
    store.set(scheduleAtomFamily(setting.id), { current: setting.schedule, baseline: setting.schedule });
  }
};

/**
 * 即架電設定の取得 Promise をクライアント単位で単一フライトキャッシュして返す（2回目以降は取得しない）。
 * 初回成功時に、編集用 atom（仕事No・スケジュール）を store へ初期化する副作用を持つ。
 * 失敗時はキャッシュを破棄するため、再マウントで再取得される。
 */
export const createCallSettingsPromise = (apiClient: ApiClient): Promise<CallSetting[]> => {
  const cached = callSettingsPromises.get(apiClient);
  if (cached !== undefined) return cached;
  const promise = fetchCallSettings(apiClient)
    .then(settings => {
      seedEditingAtoms(settings);
      return settings;
    })
    // 失敗した Promise をキャッシュし続けると、一過性の障害でもリロードまで設定画面が復旧しないため、
    // 失敗時はキャッシュを破棄して再マウント時に再取得できるようにする。
    .catch((error: unknown) => {
      callSettingsPromises.delete(apiClient);
      throw error;
    });
  callSettingsPromises.set(apiClient, promise);
  return promise;
};
